import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import api from '../api/axios';
import { Report } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  report?: Report;
}

export default function ContactErnieModal({ open, onClose, report }: Props) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    setError('');
    try {
      await api.post('/notifications/contact-ernie/', {
        ...(report ? { report_uuid: report.uuid } : {}),
        message: message.trim(),
      });
      setSent(true);
      setMessage('');
    } catch (e: any) {
      setError(e.response?.status === 429
        ? "You've sent a lot of messages in the last hour. Please try again later."
        : 'Failed to send message. Please try again.');
    }
    setSending(false);
  };

  const handleClose = () => {
    setSent(false);
    setError('');
    setMessage('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#102B3D' }}>
        Contact Ernie
      </DialogTitle>
      <DialogContent>
        {sent ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Your message has been sent. Ernie will get back to you soon.
          </Alert>
        ) : (
          <>
            {report && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#EAF1F5', borderRadius: 1, border: '1px solid #D7E1E8' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Regarding
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {report.facility_name} (CCN: {report.ccn})
                </Typography>
              </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              label="Your message"
              multiline
              rows={4}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={report ? 'I have a question about this report...' : 'How can we help?'}
              autoFocus
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: '#6B7280' }}>
          {sent ? 'Close' : 'Cancel'}
        </Button>
        {!sent && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={sending || !message.trim()}
            sx={{ bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' } }}
          >
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
